import {
  Avatar, Box, Divider, Grid, List,
  ListItem,
  ListItemAvatar, ListItemSecondaryAction, ListItemText, Typography, Chip, Tooltip
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import {
  Delete, FiberManualRecord, FiberManualRecordOutlined, Headset, PersonAdd,
  PersonRounded,
  Warning
} from '@material-ui/icons';
import {
  Button,
  ConfirmationIconButton, StandardDialog
} from '@roc/ui';
import { Participant } from '@twilio/conversations';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { CommunicationStore } from '../stores/communicationStore';
import { ParticipantAttribute, ParticipantDetails } from '../types/communicationTypes';
import { AddParticipantDialog } from './addParticipantDialog';
import { ChatRoomStatus } from "../types/communicationTypes";
import { CALL_PRESENCE_LABEL, DisplayNameWithOnlineStatus, WebSocketStore, useCallPresence } from '@roc/feature-app-core';
import { getRoleInfoForAuthor } from '../helper';

const useDialogContentStyles = makeStyles(theme => ({
  root: {
    '& .dialog-content-box': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

const useStyles = makeStyles(theme => ({
  // borderless rows; a soft rounded highlight on hover
  participantRowContainer: {
    marginBottom: 4,
    borderRadius: 12,
    '&:hover': {
      background: '#F7F8FC',
    },
  },
  chip: {
    color: 'black',
    marginLeft: '10px'
  },
  /*
   * Sits beside the delete icon, so it has to stay compact and leave that icon where it was.
   * Contained rather than outlined -- unlike the profile card's Huddle, this one appears on
   * every row at once, and a filled button greying out is the clearest way to say which of
   * nineteen people can actually be reached right now.
   */
  huddleButton: {
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  /* Reads ahead of the button it explains, so it sits to its left and never wraps under it. */
  presenceLabel: {
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  listItem: {
    '&.MuiListItemText-root': {
      flex: 'inherit',
      minWidth: 0,
      marginTop: '4px',
      marginBottom: '4px',
    },
  },
}));

export const ManageParticipantsDialog = observer(({ communicationStore, allowAddParticipants = true, allowAddInternalParticipants, allowRemoveParticipants, showEmailAddress, loanId, taskId = null, onHuddle }: {
  communicationStore: CommunicationStore,
  webSocketStore?: WebSocketStore,
  allowAddParticipants?: boolean,
  allowAddInternalParticipants: boolean,
  allowRemoveParticipants: boolean,
  showEmailAddress: boolean;
  loanId: number,
  taskId?: number,
  /**
   * Call this participant. Undefined means no Huddle button at all.
   *
   * Passed down rather than decided here: the caller already holds the one answer -- Meet being
   * available, and this role being allowed to place a call -- and chatSection hands the same
   * callback to the profile card behind a name. Two places offering a call, one rule.
   */
  onHuddle?: (inviteUserIds: number[]) => void,
}) => {
  const {
    manageParticipantsStore,
    participants,
    currentConversationSid,
    currentConversation,
  } = communicationStore;
  const participantsCount = participants[currentConversationSid]?.length ?? 0;
  /*
   * Read once for the whole list, here rather than per row: the rows share one answer, and a
   * twenty-person conversation asking twenty times would be twenty identical requests. Only when
   * a Huddle can be offered at all -- there is nothing to say about callability on a list that
   * carries no call button.
   */
  useCallPresence(!!onHuddle);

  const removeParticipant = (participant) => {
    manageParticipantsStore.removeParticipant(currentConversationSid, participant, loanId);
  };

  const handleAddNewParticipant = () => {
    manageParticipantsStore.addNewParticipant(loanId);
  };

  const handleAddExistingParticipant = () => {
    manageParticipantsStore.addExistingParticipant(loanId);
  };

  useEffect(() => {
    manageParticipantsStore.reset();
  }, []);

  return (
    <StandardDialog
      title={
        <>
          Manage Participants
          <p style={{
            fontSize: 16,
            marginTop: 8
          }}>{communicationStore.getConversationDisplayName(currentConversation)}</p>
        </>
      }
      open={participantsCount > 0 && manageParticipantsStore.open}
      handleClose={() => manageParticipantsStore.closeDialog()}
      removeContentBox={true}
      dialogContent={
        <Box flexGrow={1}>
          <Grid container spacing={2}>
            <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6">
                Participants ({participantsCount})
              </Typography>
            </Grid>
            <Grid
              item
              xs={6}
              style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              {allowAddParticipants && communicationStore.conversationChatRoomStatus[currentConversationSid] !== ChatRoomStatus.ARCHIVED && (<Button
                testId="create-new-converstation"
                variant="outlined"
                color="default"
                startIcon={<PersonAdd />}
                onClick={manageParticipantsStore.openAddParticipantDialog}
              >
                Add Participant
              </Button>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <List>
                {participants[currentConversationSid]?.map(p => (
                  <ParticipantRow
                    key={p.identity}
                    communicationStore={communicationStore}
                    participant={p}
                    allowRemoveParticipants={allowRemoveParticipants}
                    onRemove={participant => removeParticipant(participant)}
                    roles={manageParticipantsStore?.userIdParticipantDetailsMap}
                    onHuddle={onHuddle}
                  />
                ))}
              </List>
            </Grid>
            <AddParticipantDialog
              communicationStore={communicationStore}
              handleAddNewParticipant={handleAddNewParticipant}
              handleAddExistingParticipant={handleAddExistingParticipant}
              showEmailAddress={showEmailAddress}
              allowAddInternalParticipants={allowAddInternalParticipants}
              loanId={loanId}
              taskId={taskId}
            />
          </Grid>
        </Box>
      }
    />
  );
});

const ParticipantRow = observer(({ communicationStore, participant, allowRemoveParticipants, onRemove, roles, onHuddle }: {
  communicationStore: CommunicationStore,
  participant: Participant,
  allowRemoveParticipants: boolean,
  onRemove: (participant: Participant) => void,
  roles: Record<number, ParticipantDetails>;
  onHuddle?: (inviteUserIds: number[]) => void,
}) => {
  const dialogContentclasses = useDialogContentStyles();
  const classes = useStyles();
  const theme = useTheme();
  // Already loaded by the dialog above; this is the read, and it is free.
  const { presenceOf } = useCallPresence(false);
  const { isSystemUser, userId } = participant.attributes as ParticipantAttribute;
  const canRemove: boolean = !isSystemUser && allowRemoveParticipants;
  const displayName = communicationStore.getName(participant);
  const roleName = getRoleInfoForAuthor(userId.toString(), roles).role;
  const hasRole = getRoleInfoForAuthor(userId.toString(), roles).hasRole
  const roleColor = getRoleInfoForAuthor(userId.toString(), roles).roleColor;
  const isActive = getRoleInfoForAuthor(userId.toString(), roles).isActive;

  const isChatRoomActive = () => {
    return communicationStore.conversationChatRoomStatus[communicationStore.currentConversationSid] !== ChatRoomStatus.ARCHIVED;
  }

  /*
   * Who can actually be rung from this row.
   *
   * onHuddle carries the permission -- it is undefined when Meet is off or this role may not
   * place calls -- so nothing about entitlement is decided here. The rest is about this one
   * person: the system user is not a person, a deactivated account cannot answer, an archived
   * room is not somewhere a call starts, and nobody should be offered a call to themselves.
   */
  const canHuddle = !!onHuddle
    && !isSystemUser
    && isActive
    && isChatRoomActive()
    && userId !== communicationStore.userId;

  /*
   * Whether a call placed to them would actually connect, and when it would not, which reason.
   *
   * Two facts, joined by useCallPresence so that this row, the profile card, Meet's own directory
   * and the mobile app all answer it the same way. Holding a websocket session is not the same as
   * being able to answer -- a colleague reading chat in a browser reports online with nowhere for
   * an invite to ring -- and neither is being free: someone already on a call will have the
   * invite answered BUSY by their own client the moment it arrives.
   *
   * The state is on the row rather than only in a tooltip, because the question people have is
   * "can I get them" and they have it before they reach for the button, not after it refuses.
   */
  const presence = presenceOf(userId);
  const isCallable = presence === 'available';

  return (
    <ListItem ContainerProps={{
      className: classes.participantRowContainer
    }}>
      <ListItemAvatar>
        <Avatar>
          <PersonRounded />
        </Avatar>
      </ListItemAvatar>
      <ListItemText>
        <Box display="flex" alignItems="center">
          <DisplayNameWithOnlineStatus
            userId={userId}
            displayName={displayName}
            isActive={isActive} />
          {hasRole && isActive &&
            <Chip variant="default"
              size="small"
              label={roleName}
              className={classes.chip}
              style={{
                height: 18,
                backgroundColor: roleColor,
                color: roleColor ? theme.palette.getContrastText(roleColor) : 'black',
                fontWeight: 'bold'
              }} />
          }
        </Box>
      </ListItemText>
      <ListItemSecondaryAction>
        {
          canHuddle &&
          <>
            {/* Said before the button is reached for, not as an explanation after it refuses. */}
            <Typography
              variant="caption"
              className={classes.presenceLabel}
              style={{ color: presence === 'available' ? theme.palette.success.main : theme.palette.text.secondary }}
            >
              {CALL_PRESENCE_LABEL[presence]}
            </Typography>
            {/* A disabled MUI button fires no pointer events, so the tooltip needs a live wrapper
                to hang off -- otherwise the one case that needs explaining cannot explain itself. */}
            <Tooltip
              title={isCallable
                ? `Call ${displayName}`
                : presence === 'on-another-call'
                  ? `${displayName} is already on another call.`
                  : `${displayName} isn't available for calls right now.`}
              placement="top"
            >
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  testId="huddle-participant"
                  className={classes.huddleButton}
                  disabled={!isCallable}
                  startIcon={<Headset fontSize="small" />}
                  onClick={() => onHuddle([userId])}
                >
                  Huddle
                </Button>
              </span>
            </Tooltip>
          </>
        }
        {
          canRemove && isChatRoomActive() &&
          <ConfirmationIconButton
            edge="end"
            testId="remove-participant"
            confirmDialogProps={{
              title: 'Remove Participant',
              dialogContentclasses: dialogContentclasses,
              body: (
                <>
                  <Warning
                    color="error"
                    fontSize="large"
                    style={{ marginRight: '16px' }}
                  />
                  <span>
                    {`Are you sure you want to remove the participant `}
                    <strong>{displayName}</strong>
                    {` from this conversation?`}
                  </span>
                </>
              ),
              confirmButtonText: 'Yes',
            }}
            onClick={() => onRemove(participant)}
          >
            <Delete />
          </ConfirmationIconButton>
        }
      </ListItemSecondaryAction>
    </ListItem>
  );
});
